import React, { useState, useEffect } from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Create,
    SimpleForm,
    TextInput,
    SelectInput,
    ReferenceInput,
    BooleanInput,
    required,
    Toolbar,
    SaveButton,
    useRedirect,
    useTranslate,
    useNotify,
    useDataProvider,
} from 'react-admin';
import { Box, Typography, Alert, Card, CardContent, IconButton, InputAdornment, Button, Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import {
    DnsRounded,
    Build,
    Settings,
    ArrowBack,
    Security,
    CloudQueue,
    Visibility,
    VisibilityOff,
    Refresh,
    ContentCopy,
    AutoAwesome,
    Warning,
    Storage,
    BarChart,
} from '@mui/icons-material';
import { CapabilitiesInput } from './CapabilitiesInput';
import { ServerConfigInput } from './ServerConfigInput';
import { IpWhitelistFieldInput } from './IpWhitelistFieldInput';
import { generateOcppServerKeys, copyToClipboard } from '../../utils/keyGeneration';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

// Backend normalises camelCase -> snake_case before Zod validates, so error
// paths come back in snake_case for those aliased fields. Map them back to
// the form sources (most of the form already uses snake_case sources, only
// endpointUrl and ipWhitelist deviate).
const OCPP_SERVER_PATH_ALIAS: Record<string, string> = {
    endpoint_url: 'endpointUrl',
    ip_whitelist: 'ipWhitelist',
};

const mapOcppServerBackendPath = (path: string): string => {
    const stripped = path
        .replace(/^body\./, '')
        .replace(/^params\./, '')
        .replace(/^query\./, '');
    return OCPP_SERVER_PATH_ALIAS[stripped] || stripped;
};

const OCPP_SERVER_KNOWN_FIELDS = new Set([
    'name', 'description', 'endpointUrl', 'ocpp_to_backend_api_key',
    'backend_to_ocpp_api_key', 'status', 'capabilities', 'ipWhitelist',
    'config', 'elk_batch_size', 'elk_batch_timeout_ms', 'elk_max_buffer_size',
    'elk_spillover_enabled', 'elk_spillover_retention_days',
    'zero_energy_timeout_seconds', 'supabaseConnectionId',
]);

const ocppServerFieldLabelKey = (field: string): string =>
    OCPP_SERVER_KNOWN_FIELDS.has(field)
        ? `resources.ocpp-servers.fields.${field}`
        : field;

const CreateToolbar = () => {
    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: 4,
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/ocpp-servers')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.ocpp-servers.create.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word' }}>
                    {translate('resources.ocpp-servers.create.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {translate('resources.ocpp-servers.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

export const OcppServersCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();
    const [showInboundApiKey, setShowInboundApiKey] = useState(false);
    const [showOutboundApiKey, setShowOutboundApiKey] = useState(false);
    const [generatedKeys, setGeneratedKeys] = useState<{
        ocppToBackendApiKey: string;
        backendToOcppApiKey: string;
        apiKeySuffix: string;
    } | null>(null);
    const [apiKeyCopied, setApiKeyCopied] = useState(false);
    const [backendToOcppApiKeyCopied, setBackendToOcppApiKeyCopied] = useState(false);
    const [keysShown, setKeysShown] = useState(false);
    const [selectedSupabaseConnectionId, setSelectedSupabaseConnectionId] = useState<string | null>(null);
    const [elkApiKey, setElkApiKey] = useState<string | null>(null);
    const [showElkKeyDialog, setShowElkKeyDialog] = useState(false);
    const [elkKeyCopied, setElkKeyCopied] = useState(false);
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

    // Generate keys on component mount
    useEffect(() => {
        const keys = generateOcppServerKeys();
        setGeneratedKeys(keys);
        setKeysShown(true);
    }, []);

    const handleGenerateNewKeys = () => {
        const keys = generateOcppServerKeys();
        setGeneratedKeys(keys);
        setApiKeyCopied(false);
        setBackendToOcppApiKeyCopied(false);
        setKeysShown(true);
        notify('resources.ocpp-servers.notifications.keysRegenerated', { type: 'info' });
    };

    const handleCopyApiKey = async () => {
        if (generatedKeys) {
            await copyToClipboard(generatedKeys.ocppToBackendApiKey);
            setApiKeyCopied(true);
            notify('resources.ocpp-servers.notifications.ocppToBackendApiKeyCopied', { type: 'success' });
        }
    };

    const handleCopyBackendToOcppApiKey = async () => {
        if (generatedKeys) {
            await copyToClipboard(generatedKeys.backendToOcppApiKey);
            setBackendToOcppApiKeyCopied(true);
            notify('resources.ocpp-servers.notifications.backendToOcppApiKeyCopied', { type: 'success' });
        }
    };

    const handleCopyElkApiKey = async () => {
        if (elkApiKey) {
            await copyToClipboard(elkApiKey);
            setElkKeyCopied(true);
            notify('ELK API key copied to clipboard', { type: 'success' });
        }
    };

    const handleCloseElkKeyDialog = () => {
        setShowElkKeyDialog(false);
        redirect('/ocpp-servers');
    };

    // Validation rules
    const validateRequired = [required()];
    const validateUrl = [
        required(),
        (value: string) => {
            try {
                new URL(value);
                return undefined;
            } catch {
                return translate('resources.ocpp-servers.validation.invalidUrl');
            }
        }
    ];

    return (
        <Create
            title={false}
            component="div"
            redirect="list"
            mutationMode="pessimistic"
            transform={(data: any) => {
                // Strip supabaseConnectionId — handled separately via /assign endpoint, not part of OCPP server payload.
                const { supabaseConnectionId, ...serverData } = data;
                return serverData;
            }}
            mutationOptions={{
                onSuccess: async (response: any) => {
                    setServerErrors({});
                    // If a Supabase connection was selected, assign it to the server
                    if (selectedSupabaseConnectionId && response.data?.id) {
                        try {
                            const { tokenManager } = await import('../../utils/tokenManager');
                            const { API_CONFIG } = await import('../../config/api');
                            const accessToken = await tokenManager.getAccessToken();

                            const assignResponse = await fetch(
                                `${API_CONFIG.baseURL}/api/backend/supabase-connections/${selectedSupabaseConnectionId}/assign`,
                                {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${accessToken}`,
                                    },
                                    body: JSON.stringify({
                                        ocppServerId: response.data.id,
                                        isPrimary: true,
                                    }),
                                }
                            );

                            if (!assignResponse.ok) {
                                console.error('Failed to assign Supabase connection');
                            }
                        } catch (error) {
                            console.error('Failed to assign Supabase connection:', error);
                            // Non-critical error, server was created successfully
                        }
                    }

                    // Check if ELK API key was generated and show it in dialog
                    if (response.elkApiKey) {
                        setElkApiKey(response.elkApiKey);
                        setShowElkKeyDialog(true);
                    } else {
                        // No ELK key, redirect immediately
                        notify('resources.ocpp-servers.notifications.createSuccess', { type: 'success' });
                        redirect('/ocpp-servers');
                    }
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapOcppServerBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(ocppServerFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                maxWidth: '100%',
                overflow: 'hidden',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 } }}>
                    <PageHeader />

                    <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                        <Typography variant="body2">
                            {translate('resources.ocpp-servers.create.infoMessage')}
                        </Typography>
                    </Alert>

                    {/* Basic Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <DnsRounded sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.create.basicInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="name"
                                    label={translate('resources.ocpp-servers.fields.name')}
                                    validate={validateRequired}
                                    fullWidth
                                    helperText={translate('resources.ocpp-servers.create.nameHelperText')}
                                />
                                <TextInput
                                    source="description"
                                    label={translate('resources.ocpp-servers.fields.description')}
                                    fullWidth
                                    multiline
                                    rows={2}
                                    helperText={translate('resources.ocpp-servers.create.descriptionHelperText')}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Connection Configuration Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <CloudQueue sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.create.connectionConfig')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="endpointUrl"
                                    label={translate('resources.ocpp-servers.fields.endpointUrl')}
                                    validate={validateUrl}
                                    fullWidth
                                    helperText={translate('resources.ocpp-servers.create.endpointUrlHelperText')}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Supabase Connection Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <Storage sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.create.supabaseConnection')}
                                </Typography>
                            </Box>

                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    {translate('resources.ocpp-servers.create.supabaseConnectionHelperText')}
                                </Typography>
                            </Alert>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <ReferenceInput
                                    source="supabaseConnectionId"
                                    reference="supabase-connections"
                                >
                                    <SelectInput
                                        label={translate('resources.ocpp-servers.create.supabaseConnection')}
                                        optionText={(record) => `${record.name} (${record.environment})`}
                                        fullWidth
                                        helperText={translate('resources.ocpp-servers.create.supabaseConnectionSelectHelperText')}
                                        validate={validateRequired}
                                    />
                                </ReferenceInput>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Security Configuration Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <Security sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.create.securityConfig')}
                                </Typography>
                            </Box>

                            {keysShown && (
                                <Alert severity="warning" sx={{ mb: { xs: 2, sm: 3 } }}>
                                    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1 }}>
                                        <Warning sx={{ flexShrink: 0 }} />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {translate('resources.ocpp-servers.create.keysWarningTitle')}
                                            </Typography>
                                            <Typography variant="body2">
                                                {translate('resources.ocpp-servers.create.keysWarningMessage')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Alert>
                            )}

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
                                <Chip
                                    icon={<AutoAwesome />}
                                    label={translate('resources.ocpp-servers.create.keysAutoGenerated')}
                                    color="primary"
                                    variant="outlined"
                                />
                                <Button
                                    startIcon={<Refresh />}
                                    onClick={handleGenerateNewKeys}
                                    size="small"
                                    variant="text"
                                >
                                    {translate('resources.ocpp-servers.create.regenerateKeys')}
                                </Button>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* OCPP to Backend API Key Field */}
                                <Box>
                                    <TextInput
                                        source="ocpp_to_backend_api_key"
                                        label={translate('resources.ocpp-servers.fields.ocppToBackendApiKey')}
                                        fullWidth
                                        type={showInboundApiKey ? 'text' : 'password'}
                                        defaultValue={generatedKeys?.ocppToBackendApiKey}
                                        helperText={translate('resources.ocpp-servers.create.ocppToBackendApiKeyHelperText')}
                                        validate={validateRequired}
                                        InputProps={{
                                            readOnly: true,
                                            sx: { fontFamily: 'monospace' },
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip title={apiKeyCopied ? translate('resources.ocpp-servers.create.copied') : translate('resources.ocpp-servers.create.copyToClipboard')}>
                                                            <IconButton
                                                                onClick={handleCopyApiKey}
                                                                edge="end"
                                                                size="small"
                                                                color={apiKeyCopied ? 'success' : 'default'}
                                                            >
                                                                <ContentCopy />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <IconButton
                                                            onClick={() => setShowInboundApiKey(!showInboundApiKey)}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showInboundApiKey ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </Box>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    {generatedKeys && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                                            {translate('resources.ocpp-servers.create.keyWillBeDisplayedAs')}: ...{generatedKeys.apiKeySuffix}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Backend to OCPP API Key Field */}
                                <Box>
                                    <TextInput
                                        source="backend_to_ocpp_api_key"
                                        label={translate('resources.ocpp-servers.fields.backendToOcppApiKey')}
                                        fullWidth
                                        type={showOutboundApiKey ? 'text' : 'password'}
                                        defaultValue={generatedKeys?.backendToOcppApiKey}
                                        helperText={translate('resources.ocpp-servers.create.backendToOcppApiKeyHelperText')}
                                        validate={validateRequired}
                                        InputProps={{
                                            readOnly: true,
                                            sx: { fontFamily: 'monospace' },
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip title={backendToOcppApiKeyCopied ? translate('resources.ocpp-servers.create.copied') : translate('resources.ocpp-servers.create.copyToClipboard')}>
                                                            <IconButton
                                                                onClick={handleCopyBackendToOcppApiKey}
                                                                edge="end"
                                                                size="small"
                                                                color={backendToOcppApiKeyCopied ? 'success' : 'default'}
                                                            >
                                                                <ContentCopy />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <IconButton
                                                            onClick={() => setShowOutboundApiKey(!showOutboundApiKey)}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showOutboundApiKey ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </Box>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Status Configuration Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <Build sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.create.statusConfig')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <SelectInput
                                    source="status"
                                    label={translate('resources.ocpp-servers.fields.status')}
                                    choices={[
                                        { id: 'pending', name: translate('resources.ocpp-servers.statuses.pending') },
                                        { id: 'active', name: translate('resources.ocpp-servers.statuses.active') },
                                        { id: 'inactive', name: translate('resources.ocpp-servers.statuses.inactive') },
                                        { id: 'maintenance', name: translate('resources.ocpp-servers.statuses.maintenance') },
                                        { id: 'retired', name: translate('resources.ocpp-servers.statuses.retired') },
                                    ]}
                                    validate={validateRequired}
                                    fullWidth
                                    helperText={translate('resources.ocpp-servers.create.statusHelperText')}
                                    defaultValue="pending"
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* ELK Configuration Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <BarChart sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.elkConfig.title')}
                                </Typography>
                            </Box>

                            <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                                <Typography variant="body2">
                                    {translate('resources.ocpp-servers.elkConfig.description')}
                                </Typography>
                            </Alert>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <WheelSafeNumberInput
                                    source="elk_batch_size"
                                    label={translate('resources.ocpp-servers.elkConfig.batchSize')}
                                    fullWidth
                                    min={10}
                                    max={1000}
                                    defaultValue={100}
                                    helperText={translate('resources.ocpp-servers.elkConfig.batchSizeHelper')}
                                />
                                <WheelSafeNumberInput
                                    source="elk_batch_timeout_ms"
                                    label={translate('resources.ocpp-servers.elkConfig.batchTimeout')}
                                    fullWidth
                                    min={1000}
                                    max={300000}
                                    defaultValue={60000}
                                    helperText={translate('resources.ocpp-servers.elkConfig.batchTimeoutHelper')}
                                />
                                <WheelSafeNumberInput
                                    source="elk_max_buffer_size"
                                    label={translate('resources.ocpp-servers.elkConfig.maxBufferSize')}
                                    fullWidth
                                    min={1000}
                                    max={100000}
                                    defaultValue={10000}
                                    helperText={translate('resources.ocpp-servers.elkConfig.maxBufferSizeHelper')}
                                />
                                <BooleanInput
                                    source="elk_spillover_enabled"
                                    label={translate('resources.ocpp-servers.elkConfig.spilloverEnabled')}
                                    defaultValue={true}
                                    helperText={translate('resources.ocpp-servers.elkConfig.spilloverEnabledHelper')}
                                />
                                <WheelSafeNumberInput
                                    source="elk_spillover_retention_days"
                                    label={translate('resources.ocpp-servers.elkConfig.spilloverRetention')}
                                    fullWidth
                                    min={1}
                                    max={30}
                                    defaultValue={7}
                                    helperText={translate('resources.ocpp-servers.elkConfig.spilloverRetentionHelper')}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Session Management Configuration Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <AutoAwesome sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    Session Management
                                </Typography>
                            </Box>

                            <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                                <Typography variant="body2">
                                    Configure automatic handling of charging sessions when EV is not ready to charge.
                                </Typography>
                            </Alert>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <WheelSafeNumberInput
                                    source="zero_energy_timeout_seconds"
                                    label="Zero-Energy Timeout (seconds)"
                                    fullWidth
                                    min={30}
                                    max={600}
                                    defaultValue={120}
                                    helperText="Time to wait before auto-canceling a session when no energy is delivered (30-600 seconds, default: 120)"
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Advanced Configuration Section */}
                    <Card sx={{ overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <Settings sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.create.advancedConfig')}
                                </Typography>
                            </Box>

                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    {translate('resources.ocpp-servers.create.advancedConfigMessage')}
                                </Typography>
                            </Alert>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: { xs: 1.5, sm: 2 }, fontWeight: 500 }}>
                                        {translate('resources.ocpp-servers.capabilities.title')}
                                    </Typography>
                                    <CapabilitiesInput source="capabilities" />
                                </Box>

                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: { xs: 1.5, sm: 2 }, fontWeight: 500 }}>
                                        {translate('resources.ocpp-servers.config.title')}
                                    </Typography>
                                    <ServerConfigInput source="config" />
                                </Box>

                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: { xs: 1.5, sm: 2 }, fontWeight: 500 }}>
                                        {translate('resources.ocpp-servers.ipWhitelist.title')}
                                    </Typography>
                                    <IpWhitelistFieldInput source="ipWhitelist" />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>

            {/* ELK API Key Dialog */}
            <Dialog
                open={showElkKeyDialog}
                onClose={handleCloseElkKeyDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ backgroundColor: '#FFF3E0', color: '#E65100' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {translate('resources.ocpp-servers.elkKey.title', 'Save ELK API Key')}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            {translate('resources.ocpp-servers.elkKey.warningTitle', 'Important: Save this key now!')}
                        </Typography>
                        <Typography variant="body2">
                            {translate('resources.ocpp-servers.elkKey.warningMessage', 'This ELK API key will only be shown once and cannot be retrieved later. Make sure to copy and save it securely.')}
                        </Typography>
                    </Alert>

                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                        {translate('resources.ocpp-servers.elkKey.label', 'Elasticsearch API Key')}
                    </Typography>
                    <Box sx={{
                        backgroundColor: '#F5F5F5',
                        p: 2,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        position: 'relative',
                    }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {elkApiKey}
                        </Typography>
                        <Tooltip title={elkKeyCopied ? translate('resources.ocpp-servers.create.copied', 'Copied!') : translate('resources.ocpp-servers.create.copyToClipboard', 'Copy to clipboard')}>
                            <IconButton
                                onClick={handleCopyElkApiKey}
                                size="small"
                                color={elkKeyCopied ? 'success' : 'default'}
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                }}
                            >
                                <ContentCopy />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        {translate('resources.ocpp-servers.elkKey.helperText', 'The OCPP server will use this key to send logs to Elasticsearch. Configure this in your OCPP server settings.')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseElkKeyDialog}
                        variant="contained"
                        color="primary"
                        sx={{ textTransform: 'none' }}
                    >
                        {translate('resources.ocpp-servers.elkKey.confirmButton', 'I have saved the key')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Create>
    );
};